import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

// VITE_API_URL should be the base (e.g., https://api.klassic.social)
// We append /api/v1 for the versioned API path
const envApiUrl = import.meta.env.VITE_API_URL || ''
const API_BASE_URL = envApiUrl.endsWith('/api/v1')
  ? envApiUrl
  : envApiUrl
    ? `${envApiUrl}/api/v1`
    : '/api/v1'

// Cookie-based auth is now the primary method for cross-subdomain SSO
// LocalStorage tokens are kept as fallback for API clients and backward compatibility

// Create axios instance with credentials enabled for cross-subdomain cookie auth
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookie auth for cross-subdomain SSO
})

// Request interceptor to add auth token (fallback for when cookies aren't available)
api.interceptors.request.use(
  (config) => {
    // Cookies are sent automatically via withCredentials
    // Add header token as fallback for backward compatibility
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Track if we're currently refreshing to prevent concurrent refresh attempts
let isRefreshing = false

// Response interceptor to unwrap API envelope and handle token refresh
api.interceptors.response.use(
  (response) => {
    // API returns { success: true, data: {...} }, unwrap it
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      response.data = response.data.data
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // Skip retry for auth endpoints - they handle their own errors
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')

    // Always try to refresh if we get 401 (cookie auth will work even without localStorage)
    // The refresh endpoint will fail with 400 if there's no refresh token cookie
    // We don't check localStorage because we're using HttpOnly cookie auth
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && !isRefreshing) {
      originalRequest._retry = true
      isRefreshing = true

      try {
        // Try to refresh tokens using cookie-based auth
        // The refresh token is in an HttpOnly cookie, so we just make the request
        // and let the server read the cookie
        const refreshToken = localStorage.getItem('refreshToken')
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          refreshToken ? { refresh_token: refreshToken } : {},
          { withCredentials: true } // Enable cookies for refresh request
        )

        // Response.data might be wrapped in success envelope or direct
        const data = response.data?.data || response.data
        console.log('[Auth Refresh] Response data:', JSON.stringify(data).substring(0, 200))
        const { access_token, refresh_token: new_refresh_token } = data

        // Store tokens in localStorage as fallback (if returned in response)
        console.log('[Auth Refresh] access_token received:', access_token ? 'yes' : 'no')
        if (access_token) {
          localStorage.setItem('accessToken', access_token)
        }
        if (new_refresh_token) {
          localStorage.setItem('refreshToken', new_refresh_token)
        }

        isRefreshing = false

        // Create a new request config with the fresh token
        // This ensures the new token is used immediately
        const retryConfig = {
          ...originalRequest,
          headers: {
            ...originalRequest.headers,
            Authorization: access_token ? `Bearer ${access_token}` : undefined,
          },
        }
        return api(retryConfig)
      } catch (refreshError) {
        isRefreshing = false
        // Refresh failed - only redirect to login if this wasn't already on a public route
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        // Don't redirect if we're already on login/register page
        if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    // Extract API error message from response
    const apiError = error.response?.data as { error?: { message?: string; code?: string } } | undefined
    if (apiError?.error?.message) {
      // Create a new error with the API message for better error handling
      const enhancedError = new Error(apiError.error.message) as Error & { code?: string; originalError?: AxiosError }
      enhancedError.code = apiError.error.code
      enhancedError.originalError = error
      return Promise.reject(enhancedError)
    }

    return Promise.reject(error)
  }
)

// API Types
export interface ApiError {
  message: string
  code?: string
  details?: Record<string, string[]>
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

// Auth response type matching backend format
export interface AuthTokens {
  access_token: string
  refresh_token: string
}

export interface AuthResponse {
  user: User
  tokens: AuthTokens
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }),

  register: (data: RegisterData) =>
    api.post<{ user: User; tokens?: AuthTokens; status: string }>('/auth/register', data),

  loginWithGoogle: (idToken: string) =>
    api.post<AuthResponse>('/auth/google', { idToken }),

  loginWithApple: (idToken: string) =>
    api.post<AuthResponse>('/auth/apple', { idToken }),

  sendMagicLink: (email: string, purpose: 'login' | 'verify_email' = 'login') =>
    api.post('/auth/magic-link', { email, purpose }),

  verifyMagicLink: (token: string) =>
    api.post<AuthResponse>('/auth/magic-link/verify', { token }),

  logout: () => api.post('/auth/logout'),

  me: () => api.get<{ user: User; profile: unknown }>('/users/me'),

  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', data),
}

// Users API
export const usersApi = {
  getProfile: (username: string) => api.get<User>(`/users/${username}`),

  getMyProfile: () => api.get<{ user: User; profile: unknown }>('/users/me/profile'),

  updateProfile: (data: Partial<UpdateProfileData>) =>
    api.put('/users/me/profile', data),

  uploadAvatar: (file: File) => {
    const formData = new FormData()
    formData.append('avatar', file)
    return api.post<{ url: string }>('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  uploadCover: (file: File) => {
    const formData = new FormData()
    formData.append('cover', file)
    return api.post<{ url: string }>('/users/me/cover', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  search: (query: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<User>>('/users/search', { params: { q: query, page, pageSize } }),
}

// Search API
export interface SearchResults {
  users: User[]
  posts: Post[]
  hashtags: Hashtag[]
  total: number
}

export interface Hashtag {
  hashtag: string
  count: number
}

export const searchApi = {
  search: (query: string, type: 'all' | 'users' | 'posts' | 'hashtags' = 'all', limit = 20, offset = 0) =>
    api.get<SearchResults>('/search', { params: { q: query, type, limit, offset } }),

  searchUsers: (query: string, limit = 20) =>
    api.get<{ users: User[] }>('/search', { params: { q: query, type: 'users', limit } }),

  searchPosts: (query: string, limit = 20) =>
    api.get<{ posts: Post[] }>('/search', { params: { q: query, type: 'posts', limit } }),

  searchHashtags: (query: string, limit = 20) =>
    api.get<{ hashtags: Hashtag[] }>('/search', { params: { q: query, type: 'hashtags', limit } }),

  getTrendingHashtags: (limit = 10) =>
    api.get<Hashtag[]>('/search/trending/hashtags', { params: { limit } }),
}

// FeedItem represents the feed response structure
export interface FeedItem {
  post: {
    post: {
      id: string
      author_id: string
      content: string
      post_type: string
      visibility: string
      reaction_count: number
      comment_count: number
      share_count: number
      created_at: string
      updated_at: string
    }
    author?: User | null
    media?: Media[]
    like_count: number
    liked_by_me: boolean
  }
  user_reaction?: string | null
  is_saved: boolean
}

// Posts API
export const postsApi = {
  getFeed: (page = 1, pageSize = 20) =>
    api.get<FeedItem[]>('/posts/feed', { params: { page, pageSize } }),

  getUserPosts: (userId: string, limit = 20, cursor?: string) =>
    api.get<FeedItem[]>(`/posts/user/${userId}`, { params: { limit, cursor } }),

  getPost: (id: string) => api.get<Post>(`/posts/${id}`),

  createPost: (data: CreatePostData) => api.post<Post>('/posts', data),

  updatePost: (id: string, data: Partial<CreatePostData>) =>
    api.patch<Post>(`/posts/${id}`, data),

  deletePost: (id: string) => api.delete(`/posts/${id}`),

  // Simplified like/unlike endpoints
  likePost: async (postId: string) => {
    console.log('[API] Calling likePost:', postId)
    try {
      const response = await api.post(`/posts/${postId}/like`)
      console.log('[API] likePost response:', response)
      return response
    } catch (error) {
      console.error('[API] likePost error:', error)
      throw error
    }
  },

  unlikePost: async (postId: string) => {
    console.log('[API] Calling unlikePost:', postId)
    try {
      const response = await api.delete(`/posts/${postId}/like`)
      console.log('[API] unlikePost response:', response)
      return response
    } catch (error) {
      console.error('[API] unlikePost error:', error)
      throw error
    }
  },

  // Full reactions API (for future multiple reaction types)
  react: (postId: string, reactionType: string) =>
    api.post(`/posts/${postId}/reactions`, { reaction_type: reactionType }),

  removeReaction: (postId: string) =>
    api.delete(`/posts/${postId}/reactions`),

  getComments: (postId: string, limit = 20, cursor?: string) =>
    api.get<CommentWithAuthor[]>(`/posts/${postId}/comments`, { params: { limit, cursor } }),

  createComment: (postId: string, content: string, parentId?: string) =>
    api.post<Comment>(`/posts/${postId}/comments`, { content, parent_id: parentId }),

  updateComment: (commentId: string, content: string) =>
    api.put<Comment>(`/comments/${commentId}`, { content }),

  deleteComment: (commentId: string) =>
    api.delete(`/comments/${commentId}`),

  getReplies: (commentId: string, limit = 10) =>
    api.get<CommentWithAuthor[]>(`/comments/${commentId}/replies`, { params: { limit } }),

  createReply: (commentId: string, content: string) =>
    api.post<Comment>(`/comments/${commentId}/replies`, { content }),
}

// Media upload response
export interface UploadedMedia {
  id: string
  url: string
  filename: string
  mime_type: string
  file_size: number
  width?: number
  height?: number
  created_at: string
}

// Media API
export const mediaApi = {
  uploadPhotos: (files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append('file', file)
    })
    return api.post<UploadedMedia | UploadedMedia[]>('/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// Social API
export const socialApi = {
  getFriends: (userId?: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<User>>('/friends', { params: { page, pageSize } }),

  sendFriendRequest: (userId: string) =>
    api.post('/friends/requests', { friend_id: userId }),

  acceptFriendRequest: (requestId: string) =>
    api.put(`/friends/requests/${requestId}/accept`),

  rejectFriendRequest: (requestId: string) =>
    api.put(`/friends/requests/${requestId}/decline`),

  removeFriend: (userId: string) =>
    api.delete(`/friends/${userId}`),

  getFollowers: (userId?: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<User>>('/follows/followers', { params: { page, pageSize } }),

  getFollowing: (userId?: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<User>>('/follows/following', { params: { page, pageSize } }),

  follow: (userId: string) =>
    api.post(`/follows/${userId}`),

  unfollow: (userId: string) =>
    api.delete(`/follows/${userId}`),

  blockUser: (userId: string) =>
    api.post(`/users/${userId}/block`),

  unblockUser: (userId: string) =>
    api.delete(`/users/${userId}/block`),

  muteUser: (userId: string) =>
    api.post(`/users/${userId}/mute`),

  unmuteUser: (userId: string) =>
    api.delete(`/users/${userId}/mute`),
}

// Moderation API
export const moderationApi = {
  reportContent: (contentType: string, contentId: string, reason: string, description?: string) =>
    api.post('/reports', { content_type: contentType, content_id: contentId, reason, description }),
}

// Groups API
export const groupsApi = {
  getGroups: (page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<Group>>('/groups', { params: { page, pageSize } }),

  getGroup: (id: string) => api.get<Group>(`/groups/${id}`),

  createGroup: (data: CreateGroupData) => api.post<Group>('/groups', data),

  updateGroup: (id: string, data: Partial<CreateGroupData>) =>
    api.patch<Group>(`/groups/${id}`, data),

  deleteGroup: (id: string) => api.delete(`/groups/${id}`),

  joinGroup: (id: string) => api.post(`/groups/${id}/join`),

  leaveGroup: (id: string) => api.post(`/groups/${id}/leave`),

  getMembers: (id: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<GroupMember>>(`/groups/${id}/members`, { params: { page, pageSize } }),
}

// Events API
export const eventsApi = {
  getEvents: (page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<Event>>('/events', { params: { page, pageSize } }),

  getEvent: (id: string) => api.get<Event>(`/events/${id}`),

  createEvent: (data: CreateEventData) => api.post<Event>('/events', data),

  updateEvent: (id: string, data: Partial<CreateEventData>) =>
    api.patch<Event>(`/events/${id}`, data),

  deleteEvent: (id: string) => api.delete(`/events/${id}`),

  rsvp: (id: string, status: 'going' | 'maybe' | 'not_going') =>
    api.post(`/events/${id}/rsvp`, { status }),

  getAttendees: (id: string, page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<EventAttendee>>(`/events/${id}/attendees`, { params: { page, pageSize } }),
}

// Messages API
export const messagesApi = {
  getConversations: (page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<Conversation>>('/conversations', { params: { page, pageSize } }),

  getConversation: (id: string) => api.get<Conversation>(`/conversations/${id}`),

  createDirectConversation: (userId: string) =>
    api.post<Conversation>('/conversations/direct', { user_id: userId }),

  createGroupConversation: (name: string, memberIds: string[]) =>
    api.post<Conversation>('/conversations/group', { name, member_ids: memberIds }),

  // Backwards compatibility - defaults to direct conversation
  createConversation: (participantIds: string[], name?: string) => {
    if (participantIds.length === 1 && !name) {
      return api.post<Conversation>('/conversations/direct', { user_id: participantIds[0] })
    }
    return api.post<Conversation>('/conversations/group', { name: name || 'Group Chat', member_ids: participantIds })
  },

  getMessages: (conversationId: string, page = 1, pageSize = 50) =>
    api.get<PaginatedResponse<Message>>(`/conversations/${conversationId}/messages`, { params: { page, pageSize } }),

  sendMessage: (conversationId: string, content: string, type: 'text' | 'image' | 'video' | 'audio' | 'file' | 'gif' | 'sticker' | 'location' = 'text') =>
    api.post<Message>(`/conversations/${conversationId}/messages`, { type, content }),

  markAsRead: (conversationId: string, messageId: string) =>
    api.post(`/conversations/${conversationId}/read`, { message_id: messageId }),
}

// Types
export interface User {
  id: string
  username: string
  email: string
  displayName: string
  display_name?: string // alias for compatibility
  bio?: string
  avatarUrl?: string
  coverUrl?: string
  location?: string
  website?: string
  birthDate: string
  ageBracket: 'under_13' | '13_15' | '16_17' | 'adult'
  isVerified: boolean
  isPrivate: boolean
  followersCount: number
  followingCount: number
  friendsCount: number
  postsCount: number
  role?: 'user' | 'moderator' | 'admin'
  createdAt: string
}

export interface RegisterData {
  email: string
  password: string
  username: string
  displayName: string
  birthDate: string
}

export interface UpdateProfileData {
  display_name?: string
  bio?: string
  avatar_url?: string
  cover_url?: string
  location?: string
  website?: string
  instagram_handle?: string
  tiktok_handle?: string
  twitter_handle?: string
  spotify_profile?: string
}

export interface Post {
  id: string
  userId: string
  user: User
  content: string
  media: Media[]
  visibility: 'public' | 'friends' | 'private'
  reactionsCount: Record<string, number>
  commentsCount: number
  sharesCount: number
  userReaction?: string
  createdAt: string
  updatedAt: string
}

export interface Media {
  id: string
  type: 'image' | 'video' | 'audio'
  url: string
  thumbnailUrl?: string
  width?: number
  height?: number
  duration?: number
}

export interface CreatePostData {
  content?: string
  post_type: 'text' | 'photo' | 'album' | 'link' | 'ai_photo' | 'music_visual'
  visibility: 'public' | 'friends' | 'private' | 'group'
  media_ids?: string[]
}

export interface Comment {
  id: string
  post_id: string
  author_id: string
  parent_id?: string
  content: string
  reaction_count: number
  reply_count: number
  moderation_status: string
  created_at: string
  updated_at: string
}

export interface CommentWithAuthor {
  comment: Comment
  author?: User | null
  replies?: CommentWithAuthor[]
}

export interface Group {
  id: string
  name: string
  description: string
  avatarUrl?: string
  coverUrl?: string
  privacy: 'public' | 'private' | 'secret'
  membersCount: number
  createdBy: User
  userMembership?: GroupMember
  createdAt: string
}

export interface GroupMember {
  userId: string
  user: User
  role: 'admin' | 'moderator' | 'member'
  joinedAt: string
}

export interface CreateGroupData {
  name: string
  description: string
  privacy: 'public' | 'private' | 'secret'
}

export interface Event {
  id: string
  name: string
  description: string
  coverUrl?: string
  location?: string
  startTime: string
  endTime?: string
  privacy: 'public' | 'friends' | 'private'
  attendeesCount: number
  createdBy: User
  userRsvp?: 'going' | 'maybe' | 'not_going'
  createdAt: string
}

export interface EventAttendee {
  userId: string
  user: User
  status: 'going' | 'maybe' | 'not_going'
  respondedAt: string
}

export interface CreateEventData {
  name: string
  description: string
  location?: string
  startTime: string
  endTime?: string
  privacy: 'public' | 'friends' | 'private'
}

export interface Conversation {
  id: string
  type: 'direct' | 'group'
  name?: string
  avatarUrl?: string
  participants: User[]
  lastMessage?: Message
  unreadCount: number
  updatedAt: string
}

export interface Message {
  id: string
  conversationId: string
  senderId: string
  sender: User
  content: string
  type: 'text' | 'image' | 'video' | 'audio' | 'file'
  mediaUrl?: string
  isRead: boolean
  createdAt: string
}

// Notifications types
export type NotificationType =
  | 'friend_request'
  | 'friend_accepted'
  | 'post_reaction'
  | 'post_comment'
  | 'comment_reply'
  | 'mention'
  | 'new_follower'
  | 'new_message'
  | 'group_invite'
  | 'group_join'
  | 'group_post'
  | 'event_invite'
  | 'event_update'
  | 'event_reminder'
  | 'ai_job_started'
  | 'ai_job_complete'
  | 'ai_job_failed'
  | 'system_announce'
  | 'security_alert'
  | 'birthday_reminder'

export interface Notification {
  id: string
  user_id: string
  notification_type: NotificationType
  title: string
  body?: string
  image_url?: string
  action_url?: string
  action_data?: string
  actor_id?: string
  post_id?: string
  comment_id?: string
  group_id?: string
  event_id?: string
  message_id?: string
  ai_job_id?: string
  is_read: boolean
  read_at?: string
  is_seen: boolean
  seen_at?: string
  created_at: string
  expires_at?: string
}

export interface NotificationWithActor {
  notification: Notification
  actor?: User
}

export interface NotificationCounts {
  unread_count: number
  unseen_count: number
}

// Notifications API
export const notificationsApi = {
  getNotifications: (limit = 20, offset = 0) =>
    api.get<NotificationWithActor[]>('/notifications', { params: { limit, offset } }),

  getUnreadCount: () =>
    api.get<NotificationCounts>('/notifications/count'),

  markAsRead: (notificationId: string) =>
    api.post(`/notifications/${notificationId}/read`),

  markAllAsRead: () =>
    api.post('/notifications/mark-all-read'),
}

export default api
